import { useMutation } from '@tanstack/react-query';
import { cloneDeep, get, set } from 'lodash-es';
import { api } from '@/api';
import { queryClient } from '@/queries';
import {
  getSchedulerShiftItemRemover,
  getSchedulerShiftItemUpdater,
  omsScheduleQueries,
  getHrSystemConflictItemRemover,
} from '@/queries/resource/schedule';
import {
  DateRangeParams,
  ResetShiftTypeRequestBody,
  SetOMSShiftTypeRequestBody,
  SetFixationTypeRequestBody,
  PlanningProcess,
  OMSResolveHrSystemConflict,
  OMSShift,
  OMSResourcesWithShifts,
  OMSResourceRulesSettings,
  OMSRequirementsInputsSettings,
} from '@/api/apis/resources/schedule';

const handleOMSSchedulePlanningQueryUpdate = (startDayOfMonth: Date, planningData: PlanningProcess) => {
  queryClient.invalidateQueries(omsScheduleQueries.schedule(startDayOfMonth));
  queryClient.setQueryData(omsScheduleQueries.planningProcess(planningData.id).queryKey, planningData);
};

const getOMSShiftFromSetBody = (body: SetOMSShiftTypeRequestBody): OMSShift => {
  const {
    scheduleId,
    resourceId,
    day,
    shiftStatus,
    isEditable,
    fixationType,
    isPreSchedule,
    slotsPerDay,
    slot1,
    slot2,
  } = body;

  const mapSlot = (slot: typeof slot1, slotIndex: number) => {
    if (!slot) return null;
    return {
      slotIndex,
      ofSlotsPerDay: slotsPerDay,
      shiftType: slot.shiftType,
      organs: slot.organs ?? null,
      isVoluntary: slot.isVoluntary ?? false,
    };
  };

  return {
    scheduleId,
    resourceId,
    day,
    shiftStatus,
    isEditable,
    fixationType,
    isPreSchedule,
    shiftType: undefined as never,
    slot1: mapSlot(slot1, 1),
    slot2: mapSlot(slot2, 2),
  };
};

export const getOMSSchedulerShiftItemUpdater =
  (payload: SetOMSShiftTypeRequestBody) =>
  (data?: OMSResourcesWithShifts): OMSResourcesWithShifts | undefined => {
    if (data) {
      const cloned = cloneDeep(data);
      const path = [payload.resourceId, 'days', payload.day];
      const shift: OMSShift = get(cloned, path);
      const newShift = getOMSShiftFromSetBody(payload);

      const updated: OMSShift = {
        ...shift,
        ...newShift,
        // eslint-disable-next-line no-nested-ternary
        slot1: payload.slot1 === null ? null : payload.slot1 === undefined ? shift?.slot1 : newShift.slot1,
        // eslint-disable-next-line no-nested-ternary
        slot2: payload.slot2 === null ? null : payload.slot2 === undefined ? shift?.slot2 : newShift.slot2,
      };

      set(cloned, path, updated);

      return cloned;
    }
    return undefined;
  };

export const useOMSScheduleSetShiftTypeOptimisticMutation = (
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  const { queryKey } = omsScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined);

  return useMutation({
    mutationFn: (body: SetOMSShiftTypeRequestBody) => api.schedule.oms.setOMSShiftType(body),
    onMutate: (body) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, getOMSSchedulerShiftItemUpdater(body));

      return prevSnapshot;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKey, getSchedulerShiftItemUpdater(data));
      queryClient.invalidateQueries(omsScheduleQueries.validation(variables.scheduleId));
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};

export const useOMSScheduleResetShiftTypeOptimisticMutation = (
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  const { queryKey } = omsScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined);

  return useMutation({
    mutationFn: (body: ResetShiftTypeRequestBody) => api.schedule.oms.resetOMSShiftType(body),
    onMutate: (body) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, getSchedulerShiftItemRemover(body));

      return prevSnapshot;
    },
    onSuccess: (_d, variables) => {
      queryClient.invalidateQueries(omsScheduleQueries.validation(variables.scheduleId));
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};

export const useOMSCreatePlanningMutation = () => {
  return useMutation({
    mutationFn: (startDayOfMonth: Date) => api.schedule.oms.createOMSPlanningProcess(startDayOfMonth),
    onSuccess: (data, startDayOfMonth) => {
      handleOMSSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useOMSRegeneratePlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: (processId: string) => api.schedule.oms.regenerateOMSPlanningProcess(processId),
    onSuccess: (data) => {
      handleOMSSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useOMSCancelPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: (processId: string) => api.schedule.oms.cancelOMSPlanningProcess(processId),
    onSuccess: (data) => {
      handleOMSSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useOMSPublishPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: ({ processId, scheduleId }: { processId: string; scheduleId: string }) =>
      api.schedule.oms.publishOMSPlanningProcess(processId, scheduleId),
    onSuccess: (data) => {
      handleOMSSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useOMSCancelGenerationPlanningMutation = (startDayOfMonth: Date) => {
  return useMutation({
    mutationFn: ({ processId, jobId }: { processId: string; jobId: string }) =>
      api.schedule.oms.cancelGenerationOMSPlanningProcess(processId, jobId),
    onSuccess: (data) => {
      handleOMSSchedulePlanningQueryUpdate(startDayOfMonth, data);
    },
  });
};

export const useOMSScheduleSyncWorkdayMutation = (
  startDayOfMonth: Date,
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  return useMutation({
    mutationFn: (scheduleId: string) => api.schedule.oms.omsScheduleRefreshHr(scheduleId),
    onSuccess: (_, scheduleId) => {
      const planningProcessId = isPlanned ? processId : undefined;
      queryClient.invalidateQueries({ queryKey: omsScheduleQueries.shifts(dateRange, planningProcessId).queryKey });
      queryClient.invalidateQueries({ queryKey: omsScheduleQueries.schedule(startDayOfMonth).queryKey });
      queryClient.invalidateQueries({ queryKey: omsScheduleQueries.planningProcess(processId).queryKey });
      queryClient.invalidateQueries({ queryKey: omsScheduleQueries.hrSystemConflicts(scheduleId).queryKey });
    },
  });
};

export const useOMSHrSystemConflictResolveMutation = (dateRange: DateRangeParams, scheduleId?: string) => {
  return useMutation({
    mutationFn: (payload: OMSResolveHrSystemConflict) => api.schedule.oms.resolveOMSScheduleHrSystemConflict(payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        omsScheduleQueries.hrSystemConflicts(scheduleId).queryKey,
        getHrSystemConflictItemRemover(variables.conflictId),
      );
      queryClient.setQueryData(omsScheduleQueries.shifts(dateRange).queryKey, getSchedulerShiftItemUpdater(data));
    },
  });
};

export const useOMSSetScheduleSettingsRulesMutation = () => {
  return useMutation({
    mutationFn: (body: OMSResourceRulesSettings) => api.schedule.oms.setOMSScheduleRules(body),
    meta: {
      invalidates: [omsScheduleQueries.rules().queryKey],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(omsScheduleQueries.rules().queryKey, data);
    },
  });
};

export const useOMSSetScheduleSettingsInputsMutation = () => {
  return useMutation({
    mutationFn: (body: OMSRequirementsInputsSettings) => api.schedule.oms.setOMSScheduleInputs(body),
    meta: {
      invalidates: [omsScheduleQueries.inputs().queryKey],
    },
    onSuccess: (data) => {
      queryClient.setQueryData(omsScheduleQueries.inputs().queryKey, data);
    },
  });
};

export const useOMSSetFixationTypeOptimisticMutation = (
  dateRange: DateRangeParams,
  processId?: string | null,
  isPlanned?: boolean,
) => {
  const { queryKey } = omsScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined);

  return useMutation({
    mutationFn: (body: SetFixationTypeRequestBody) => api.schedule.oms.setOMSFixationType(body),
    onMutate: (body) => {
      const prevSnapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(
        queryKey,
        getSchedulerShiftItemUpdater({ ...body, fixationType: body.fixationType, isEditable: true } as OMSShift),
      );
      return prevSnapshot;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(omsScheduleQueries.validation(variables.scheduleId));
    },
    onError: (_e, _p, context) => {
      queryClient.setQueryData(queryKey, context);
    },
  });
};
